import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IRuleCalculationFields } from 'src/types/priceRules';
import { StatusBar, SnackbarStatus, Typography } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';

import styles from 'src/components/PriceRuleFlowShared/PriceRule.module.scss';
import PriceRuleAdjustmentOptions from '../PriceRuleAdjustmentOptions';
import { RoundingRuleOptions } from '../RoundingRuleOptions';
import PriceRuleOverview from '../PriceRuleFlowShared/PriceRuleOverview';
import {
  buildCalculationSubtitle,
  buildRoundingSubtitle,
  buildExistingRuleSubtitle,
  getTitles,
  TPriceRuleTitleStep,
} from 'src/utils/priceRules/confirmationStrings';
import { createPriceRules } from 'src/api/createPriceRules';
import { getChannelLabel, IPricingChannel } from 'src/helpers/channels/getPricingChannels';
import {
  createPriceRuleAdjustment,
  createPriceRuleRounding,
  defaultAdjustmentValues,
  defaultRoundingValues,
} from 'src/utils/priceRules/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { CREATE_CHANNEL_RULE_NOTIFICATION, UPDATE_CHANNEL_RULE_NOTIFICATION } from './constants';
import { NO_CHANNEL_RULE_DEFINED } from 'src/components/PriceSchemeChannelRulesTab/constants';
import { AdjustmentType, IPriceRule, RoundingType } from 'src/types/price.types';

import { updatePriceRule } from 'src/api/updatePriceRule/updatePriceRule';
import PriceRuleContinueButtons from '../PriceRuleFlowShared/PriceRuleContinueButtons';
import { IntegrationType } from 'src/constants';
import { useRules } from 'src/hooks/useRules/useRules';
import SelectableListWithControls from '../SelectableListWithControls';

export type ChannelRuleModalContentProps = {
  channels: IPricingChannel[];
  closeModal: () => void;
  priceSchemeId: string;
  priceRuleToEdit?: IPriceRule;
  defaultStep?: number;
  preSelectedChannelId?: string | null;
};

const RULE_ONLY_TOTAL_STEPS = 3;
const FULL_FLOW_TOTAL_STEPS = 4;

export enum ChannelRuleSteps {
  Channels = 0,
  Adjustment = 1,
  Rounding = 2,
  Confirm = 3,
}

const isContinueButtonDisabled = (
  step: ChannelRuleSteps,
  { rounding, adjustment, entities }: IRuleCalculationFields,
): boolean => {
  switch (step) {
    case ChannelRuleSteps.Channels:
      return !entities.length;
    case ChannelRuleSteps.Adjustment: {
      const adjustmentEnabled = adjustment.type !== AdjustmentType.NONE;
      const noValueExists = Number.isNaN(adjustment.fixed) && Number.isNaN(adjustment.percentage);

      return adjustmentEnabled && noValueExists;
    }
    case ChannelRuleSteps.Rounding: {
      const roundingEnabled = rounding.type !== RoundingType.NONE && typeof rounding.method !== 'undefined';
      const noValueExists = typeof rounding.value === 'undefined' || Number.isNaN(rounding.value);
      return roundingEnabled && noValueExists;
    }
    default:
      return false;
  }
};

const createSteps: TPriceRuleTitleStep[] = [
  { title: 'Select Channel', subtitle: 'Select the channel you would like to create a price automation rule for.' },
  {
    title: 'Channel Calculation',
    subtitle: 'Automate pricing adjustments for this channel based on default product values.',
  },
  {
    title: 'Channel Rounding',
    subtitle: 'Create a rounding rule for all automated price changes within this price band.',
  },
];

const channelToOption = (channel: IPricingChannel, existingRule?: IPriceRule) => ({
  id: channel.extensionInstanceId,
  label: getChannelLabel(channel),
  subtitle: existingRule ? buildExistingRuleSubtitle(existingRule, NO_CHANNEL_RULE_DEFINED) : 'No rules',
  disabled: !!existingRule,
});

const ChannelRuleModalContent = ({
  priceRuleToEdit,
  closeModal,
  priceSchemeId,
  channels = [],
  defaultStep,
  preSelectedChannelId,
}: ChannelRuleModalContentProps) => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [step, setStep] = useState<number>(defaultStep ?? ChannelRuleSteps.Channels);
  const { data: priceRules = [] } = useRules(priceSchemeId);
  const existingChannelRuleMap = useMemo(
    () =>
      new Map(
        priceRules
          .filter((rule) => rule.extension_instance && !rule.pricing_category && !rule.pricing_category_id)
          .map((rule) => [rule.extension_instance.id, rule]),
      ),
    [priceRules],
  );

  const defaultRounding = defaultRoundingValues(priceRuleToEdit?.rounding);
  const defaultAdjustment = defaultAdjustmentValues(priceRuleToEdit?.adjustment);

  const { handleSubmit, register, formState, setValue, watch, control } = useForm<IRuleCalculationFields>({
    defaultValues: {
      adjustment: defaultAdjustment,
      rounding: defaultRounding,
      entities: preSelectedChannelId ? [preSelectedChannelId] : [],
    },
  });

  const { errors, isSubmitting } = formState;
  const { entities, rounding, adjustment } = watch();

  const isEditMode = priceRuleToEdit && defaultStep !== undefined;
  // Entering at the adjustment step means the channel is already chosen, so the picker step is dropped.
  const isRuleOnlyFlow = (defaultStep ?? ChannelRuleSteps.Channels) >= ChannelRuleSteps.Adjustment;
  const totalSteps = isRuleOnlyFlow ? RULE_ONLY_TOTAL_STEPS : FULL_FLOW_TOTAL_STEPS;
  const displayedStep = isRuleOnlyFlow ? step - ChannelRuleSteps.Adjustment : step;
  const showConfirmBtn = step === ChannelRuleSteps.Confirm || isEditMode;
  const showCancelBtn = step === (defaultStep ?? ChannelRuleSteps.Channels) || isEditMode;
  const continueDisabled = isContinueButtonDisabled(step, { entities, rounding, adjustment });

  const integrations = channels.filter((channel) => channel.integrationType === IntegrationType.DELIVERY_SERVICE);

  const roundingSubtitle = useMemo(() => buildRoundingSubtitle(rounding), [step, rounding]);
  const calculationSubtitle = useMemo(() => buildCalculationSubtitle(adjustment), [step, adjustment]);

  const checkboxOptions = useMemo(
    () =>
      integrations.map((channel) => {
        const existingRule = existingChannelRuleMap.get(channel.extensionInstanceId);
        return channelToOption(channel, existingRule);
      }),
    [integrations, existingChannelRuleMap],
  );

  const onChannelChange = (channels: string[]) => setValue('entities', channels);

  const decrementStep = () => setStep((s) => s - 1);
  const incrementStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setStep((s) => s + 1);
  };

  const onCreate = async ({ entities, rounding, adjustment }: IRuleCalculationFields) => {
    const requestBody = entities.map((extensionInstanceId) => ({
      extension_instance_id: extensionInstanceId,
      adjustment: createPriceRuleAdjustment(adjustment),
      rounding: createPriceRuleRounding(rounding),
    }));
    await createPriceRules(requestBody, priceSchemeId);
  };

  const onUpdate = async (fields: IRuleCalculationFields) => {
    await updatePriceRule(
      [
        {
          price_rule_id: priceRuleToEdit.price_rule_id,
          adjustment: createPriceRuleAdjustment(fields.adjustment),
          rounding: createPriceRuleRounding(fields.rounding),
        },
      ],
      priceSchemeId,
    );
  };

  const onSubmit = async (fields: IRuleCalculationFields) => {
    clearError();
    try {
      if (isEditMode) {
        await onUpdate(fields);
        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: UPDATE_CHANNEL_RULE_NOTIFICATION.SUCCESS,
        });
      } else {
        await onCreate(fields);
        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: CREATE_CHANNEL_RULE_NOTIFICATION.SUCCESS,
        });
      }

      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey.at(1) === QueryKeys.priceBands || queryKey.at(1) === QueryKeys.priceRules,
      });
      closeModal();
    } catch (error) {
      const message = error instanceof Error ? error.message : UPDATE_CHANNEL_RULE_NOTIFICATION.ERROR;

      showNotification({
        status: SnackbarStatus.ERROR,
        message,
      });
    }
  };

  const [title, subtitle] = getTitles(step, createSteps);
  const selectedChannels = channels.filter((c) => entities.includes(c.extensionInstanceId));

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="channe-rule-form" aria-label="channel-rule-form">
      {!isEditMode && (
        <Typography className={styles['steps']}>{`Step ${displayedStep + 1} of ${totalSteps}`}</Typography>
      )}
      <Typography className={styles['title']} size="xxlarge" weight="semi-bold">
        {title}
      </Typography>
      <Typography className={styles['subtitle']}>{subtitle}</Typography>

      {step === ChannelRuleSteps.Channels && (
        <SelectableListWithControls
          options={checkboxOptions}
          onOptionChange={onChannelChange}
          selectedOptionsIds={entities}
          listDetails={{ title: 'Channels:' }}
        />
      )}

      {step === ChannelRuleSteps.Adjustment && (
        <PriceRuleAdjustmentOptions register={register as any} control={control as any} entity="Rule" />
      )}

      {step === ChannelRuleSteps.Rounding && (
        <RoundingRuleOptions register={register as any} control={control as any} errors={errors} />
      )}

      {step === ChannelRuleSteps.Confirm && (
        <PriceRuleOverview
          name="Channel(s)"
          sections={[
            {
              title: 'Channel Allocation',
              subtitle: `${selectedChannels.length} channel(s) selected`,
              children: (
                <ul>
                  {selectedChannels.map((channel) => (
                    <li key={channel.extensionInstanceId}>{channel.name}</li>
                  ))}
                </ul>
              ),
            },
            { title: 'Calculation', subtitle: calculationSubtitle },
            { title: 'Rounding', subtitle: roundingSubtitle },
          ]}
        />
      )}

      <PriceRuleContinueButtons
        showCancelBtn={showCancelBtn}
        closeModal={closeModal}
        decrementStep={decrementStep}
        showConfirmBtn={showConfirmBtn}
        isSubmitting={isSubmitting}
        continueDisabled={continueDisabled}
        incrementStep={incrementStep}
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export default ChannelRuleModalContent;
